import { registerPlugin } from '@capacitor/core';

export interface StoragePermissionStatus {
  granted: boolean;
  platform?: string;
  sdkInt?: number;
  requiresSettings?: boolean;
  permissionType?: 'manage_all_files' | 'legacy_external_storage' | string;
  openedSettings?: boolean;
}

interface D3DirectoryResult {
  ready: boolean;
  reason?: string;
}

interface D3DirExistsResult {
  exists: boolean;
}

interface StoragePermissionPlugin {
  checkStatus(): Promise<StoragePermissionStatus>;
  requestAccess(): Promise<StoragePermissionStatus>;
  checkD3LogsDir(): Promise<D3DirExistsResult>;
  checkD3UpgradeDir(): Promise<D3DirExistsResult>;
  ensureD3Directories(): Promise<D3DirectoryResult>;
}

const StoragePermission = registerPlugin<StoragePermissionPlugin>('StoragePermission');

/**
 * Android 外部存储权限服务。
 *
 * 这里将平台差异收口到一个入口：
 * - Android 11 及以上：显式引导到“所有文件访问权限”设置页。
 * - Android 10 及以下：走系统运行时读写权限申请。
 */
export async function getAndroidStoragePermissionStatus(): Promise<StoragePermissionStatus> {
  return await StoragePermission.checkStatus();
}

export async function requestAndroidStoragePermission(): Promise<StoragePermissionStatus> {
  return await StoragePermission.requestAccess();
}

/** Android：用原生 File API 检查 Download/d3/logs（避免 Capacitor ExternalStorage 在 11+ 误报） */
export async function checkAndroidD3LogsDirExists(): Promise<boolean> {
  const { exists } = await StoragePermission.checkD3LogsDir();
  return !!exists;
}

/** Android：用原生 File API 检查 Download/d3/upgrade */
export async function checkAndroidD3UpgradeDirExists(): Promise<boolean> {
  const { exists } = await StoragePermission.checkD3UpgradeDir();
  return !!exists;
}

/** Android：用原生 File API 创建 Download/d3 目录树 */
export async function ensureAndroidD3Directories(): Promise<boolean> {
  const { ready } = await StoragePermission.ensureD3Directories();
  return !!ready;
}
